import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import api from "../lib/api";

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

const WS_URL = () => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return base.replace(/^http/, "ws") + "/api/ws/signal";
};

export const CallProvider = ({ children }) => {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const handlersRef = useRef(new Set());
  const reconnectRef = useRef(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [wsReady, setWsReady] = useState(false);

  const registerHandler = useCallback((fn) => {
    handlersRef.current.add(fn);
    return () => handlersRef.current.delete(fn);
  }, []);

  const sendSignal = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(obj));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!user) return;
    let closed = false;

    const connect = async () => {
      let ticket;
      try {
        const { data } = await api.get("/webrtc/ws-ticket");
        ticket = data.ticket;
      } catch (e) {
        if (!closed) reconnectRef.current = setTimeout(connect, 5000);
        return;
      }
      const ws = new WebSocket(WS_URL() + "?token=" + ticket);
      wsRef.current = ws;
      ws.onopen = () => setWsReady(true);
      ws.onclose = () => {
        setWsReady(false);
        if (!closed) reconnectRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (ev) => {
        let data;
        try { data = JSON.parse(ev.data); } catch (e) { return; }
        if (data.type === "offer") {
          setIncomingCall((cur) => cur || {
            from: data.from,
            sdp: data.sdp,
            match_id: data.match_id,
            caller_name: data.caller_name,
            caller_photo: data.caller_photo,
          });
        }
        handlersRef.current.forEach((fn) => {
          try { fn(data); } catch (e) {}
        });
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [user]);

  return (
    <CallContext.Provider value={{ sendSignal, registerHandler, incomingCall, setIncomingCall, wsReady }}>
      {children}
    </CallContext.Provider>
  );
};
